import React, { useState, useRef } from 'react';
import { ArrowRight, ShieldCheck, Award, MapPin, Loader2, Send, CheckCircle2, AlertCircle, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { companyData, heroBgUrl } from '../data';

export default function Hero() {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/kontakt');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const form = formRef.current;
      const formData = new FormData();

      // Append standard fields
      formData.append('name', form.elements.hero_name.value);
      formData.append('email', form.elements.hero_email.value);
      formData.append('phone', form.elements.hero_phone.value);
      formData.append('message', form.elements.hero_message.value);
      
      // Honeypot field (anti-spam)
      formData.append('website_url', form.elements.website_url.value);

      // Submit to specified endpoint
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status !== 'ok') {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }

      setSuccess(true);
      form.reset();
    } catch (err) {
      console.error('Hero form submission error:', err);
      setError(err.message || 'Der skete en uventet fejl. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="forside" className="relative min-h-[85vh] lg:min-h-[85vh] xl:min-h-[80vh] flex items-center justify-center pt-28 pb-16 overflow-hidden bg-[#111111]">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBgUrl}
          alt="Bust Byg & Renovering"
          className="w-full h-full object-cover object-center scale-105 animate-subtle-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/50 to-transparent" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24 lg:py-20 text-center lg:text-left w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-none uppercase">
              Bust Byg &amp;<br />Renovering ApS
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-white max-w-2xl font-normal leading-relaxed mx-auto lg:mx-0">
              {companyData.tagline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4 pt-4">
              <a
                href="/kontakt"
                onClick={handleContactClick}
                className="group flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-none text-base font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
              >
                <span>Få et tilbud</span>
                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a
                href={`tel:${companyData.phoneRaw}`}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#111111] px-8 py-4 rounded-none text-base font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
              >
                <Phone className="w-5 h-5 text-black" fill="currentColor" />
                <span>{companyData.phone}</span>
              </a>
            </div>
          </div>

          {/* Side Card (Contact Form) */}
          <div className="lg:col-span-5 w-full">
            <style>{`
              @keyframes subtle-shake {
                0%, 100% { transform: rotate(0deg) translate(0, 0); }
                15% { transform: rotate(-1deg) translate(-2px, 2px); }
                30% { transform: rotate(1deg) translate(2px, -2px); }
                45% { transform: rotate(-1deg) translate(-2px, -2px); }
                60% { transform: rotate(1deg) translate(2px, 2px); }
                75% { transform: rotate(-0.5deg) translate(-1px, 1px); }
                90% { transform: rotate(0.5deg) translate(1px, -1px); }
              }
              .animate-shake-once {
                animation: subtle-shake 0.8s ease-in-out;
              }
            `}</style>
            <div className="bg-white border border-gray-200 p-6 sm:p-8 rounded-none shadow-2xl space-y-5 text-left animate-shake-once transition-all duration-300">
              <div className="border-b border-gray-100 pb-3">
                <h3 className="text-xl font-bold text-gray-900 uppercase">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Udfyld formularen, så vender vi hurtigt tilbage til dig.
                </p>
              </div>

              {success ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-6 rounded-lg text-center space-y-3 py-8">
                  <div className="inline-flex p-2.5 rounded-full bg-emerald-500/20 text-emerald-600">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 uppercase">Besked modtaget!</h4>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    Tak for din henvendelse. Vi kontakter dig hurtigst muligt for at drøfte dit projekt.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-none text-xs font-bold uppercase tracking-wider transition-colors"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Error Notification */}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-md flex items-start gap-2 text-red-600 text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>{error}</p>
                    </div>
                  )}

                  {/* Name */}
                  <div className="space-y-1.5">
                    <label htmlFor="hero_name" className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="hero_name"
                      name="hero_name"
                      required
                      placeholder="Dit navn"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded px-3 py-2 text-gray-900 text-xs placeholder-gray-400 outline-none transition-colors"
                    />
                  </div>

                  {/* Grid for Phone and Email */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Phone */}
                    <div className="space-y-1.5">
                      <label htmlFor="hero_phone" className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        Telefon <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="hero_phone"
                        name="hero_phone"
                        required
                        placeholder="F.eks. 22800015"
                        className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded px-3 py-2 text-gray-900 text-xs placeholder-gray-400 outline-none transition-colors"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <label htmlFor="hero_email" className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="hero_email"
                        name="hero_email"
                        required
                        placeholder="din@email.dk"
                        className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded px-3 py-2 text-gray-900 text-xs placeholder-gray-400 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label htmlFor="hero_message" className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                      Hvad skal du have lavet? <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="hero_message"
                      name="hero_message"
                      required
                      rows="3"
                      placeholder="Beskriv kort dit projekt..."
                      className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded px-3 py-2 text-gray-900 text-xs placeholder-gray-400 outline-none transition-colors resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white py-3 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-200"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send forespørgsel</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Decorative Angle */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#111111] to-transparent pointer-events-none" />
    </section>
  );
}
